/**
 * CookieConsentBanner — J-X14 contract.
 *
 * Mounted at App root. Renders only when the `cookie_consent` cookie is
 * missing. On choice, persists the cookie for 12 months and dismisses.
 *
 * Choices:
 *   - "Accept all" → cookie_consent=all → analytics scripts may load
 *   - "Essential only" → cookie_consent=essential → no analytics ever
 *   - "Customise" → opens the same modal the footer's "Cookie preferences"
 *     link opens (the banner and the modal share state via the
 *     `bimassess:cookie-preferences` custom event on `window`).
 *
 * The Footer button dispatches `bimassess:cookie-preferences`; this banner
 * listens for it and opens the modal even after a previous consent choice
 * has dismissed the inline banner (J-X14 § Steps consent withdrawal).
 *
 * Inv 12 (no console.log), Inv 13 (no any), Inv 14 (Link from barrel for
 * /legal/cookies link), Inv 15 (Button atom from barrel).
 */

import { useEffect, useState, type ReactElement } from 'react';
import { Button, Link } from '@/components/ui';
import { COOKIE_PREFERENCES_EVENT } from './app-shell/Footer.tsx';

export type CookieConsentValue = 'all' | 'essential';

const COOKIE_NAME = 'cookie_consent';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 12 months

function readConsentCookie(): CookieConsentValue | null {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';').map((c) => c.trim());
  for (const c of cookies) {
    if (c.startsWith(`${COOKIE_NAME}=`)) {
      const value = c.slice(COOKIE_NAME.length + 1);
      if (value === 'all' || value === 'essential') return value;
      return null;
    }
  }
  return null;
}

function writeConsentCookie(value: CookieConsentValue): void {
  if (typeof document === 'undefined') return;
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const parts = [
    `${COOKIE_NAME}=${value}`,
    'Path=/',
    `Max-Age=${String(COOKIE_MAX_AGE_SECONDS)}`,
    'SameSite=Lax',
  ];
  if (secure) parts.push('Secure');
  document.cookie = parts.join('; ');
}

export function CookieConsentBanner(): ReactElement | null {
  // `null` while we read the cookie on mount to avoid a banner flash for
  // returning visitors. Once known, `null` means "consent already set" so
  // the inline banner stays hidden.
  const [inlineState, setInlineState] = useState<CookieConsentValue | null | undefined>(
    undefined,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [modalChoice, setModalChoice] = useState<CookieConsentValue>('essential');

  useEffect(() => {
    setInlineState(readConsentCookie());
  }, []);

  useEffect(() => {
    function onPrefs(): void {
      setModalChoice(readConsentCookie() ?? 'essential');
      setModalOpen(true);
    }
    if (typeof window === 'undefined') return undefined;
    window.addEventListener(COOKIE_PREFERENCES_EVENT, onPrefs);
    return () => {
      window.removeEventListener(COOKIE_PREFERENCES_EVENT, onPrefs);
    };
  }, []);

  function choose(value: CookieConsentValue): void {
    writeConsentCookie(value);
    setInlineState(value);
    setModalOpen(false);
  }

  function saveModal(): void {
    writeConsentCookie(modalChoice);
    setInlineState(modalChoice);
    setModalOpen(false);
  }

  // Inline banner: only renders for first-visit (consent unknown).
  const showInline = inlineState === null;

  return (
    <>
      {showInline ? (
        <div
          role="region"
          aria-label="Cookie consent"
          className="fixed inset-x-0 bottom-0 z-50 border-t border-line bg-card shadow-card"
        >
          <div className="mx-auto flex max-w-page flex-col gap-4 px-page-pad-x py-5 md:flex-row md:items-center md:justify-between">
            <p className="font-sans text-body text-ink">
              We use essential cookies to keep the site working. With your
              permission we also use analytics cookies to improve the product.{' '}
              <Link to="/legal/cookies" className="no-underline">
                Read more
              </Link>
              .
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => {
                  setModalChoice('essential');
                  setModalOpen(true);
                }}
              >
                Customise
              </Button>
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => choose('essential')}
              >
                Essential only
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="button"
                onClick={() => choose('all')}
              >
                Accept all
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {modalOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-pref-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/50 px-4"
        >
          <div className="w-full max-w-md rounded-lg border border-line bg-card p-6 shadow-card">
            <h2
              id="cookie-pref-title"
              className="font-sans text-h3 font-medium tracking-tight"
            >
              Cookie preferences
            </h2>
            <p className="mt-3 font-sans text-body text-muted">
              Essential cookies are always on (they keep the site working).
              Toggle analytics to help us improve the product.
            </p>

            <fieldset className="mt-6 space-y-3">
              <legend className="sr-only">Cookie choice</legend>
              <label className="flex items-start gap-3">
                <input
                  type="radio"
                  name="cookie-choice"
                  value="essential"
                  checked={modalChoice === 'essential'}
                  onChange={() => setModalChoice('essential')}
                  className="mt-1.5"
                />
                <span className="font-sans text-body text-ink">
                  <strong>Essential only</strong>
                  <span className="block font-mono text-mono text-muted-2">
                    Only cookies strictly necessary to deliver the service.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3">
                <input
                  type="radio"
                  name="cookie-choice"
                  value="all"
                  checked={modalChoice === 'all'}
                  onChange={() => setModalChoice('all')}
                  className="mt-1.5"
                />
                <span className="font-sans text-body text-ink">
                  <strong>Essential + analytics</strong>
                  <span className="block font-mono text-mono text-muted-2">
                    Allow anonymised analytics cookies to help us improve.
                  </span>
                </span>
              </label>
            </fieldset>

            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="button"
                onClick={saveModal}
              >
                Save preferences
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
